/**
 * useMenuNav — navigazione "legacy-aware" verso una pagina.
 *
 * Data una `springRoute` (colonna funzioni_2.spring_route) e il `routeName` Vue
 * corrispondente, decide dove andare leggendo il menu dell'utente (menuStore):
 *  - se la voce di menu è marcata legacy (`is_legacy=1`, cioè la funzione NON è ancora
 *    migrata/attiva in Vue) → apre la pagina Seam legacy (`home.seam?pagina=...`);
 *  - altrimenti → naviga alla route Vue.
 *
 * È lo stesso criterio usato dai KPI della dashboard (DashboardView.navigateKpiToPage):
 * la sorgente di verità è `is_legacy` in funzioni_2, quindi il comportamento è
 * data-driven e cambia da solo quando una funzione viene migrata (flip del flag).
 */
import { useRouter } from 'vue-router'
import { useMenuStore, type MenuNode } from '@/stores/menu'
import { useFunzioniRouting } from '@/composables/useFunzioniRouting'

export function useMenuNav() {
  const router = useRouter()
  const menuStore = useMenuStore()
  const funzioni = useFunzioniRouting()
  const LEGACY_BASE = import.meta.env.VITE_LEGACY_BASE_URL ?? '/legacy'

  /** Cerca ricorsivamente il nodo di menu per spring_route. */
  function findNodeBySpringRoute(springRoute: string): MenuNode | null {
    function search(nodes: MenuNode[]): MenuNode | null {
      for (const node of nodes) {
        if (node.springRoute === springRoute) return node
        if (node.children?.length) {
          const found = search(node.children)
          if (found) return found
        }
      }
      return null
    }
    return search(menuStore.items)
  }

  /** Apre una pagina Seam nella nuova scheda (formato home.seam?pagina=...). */
  function openLegacyPage(xhtmlPath: string): void {
    const base = LEGACY_BASE.endsWith('/') ? LEGACY_BASE.slice(0, -1) : LEGACY_BASE
    const pagina = xhtmlPath.startsWith('/') ? xhtmlPath.slice(1) : xhtmlPath
    window.open(`${base}/home.seam?pagina=${encodeURIComponent(pagina)}`, '_blank')
  }

  /**
   * Naviga alla route Vue se la funzione è migrata (is_legacy=0), altrimenti apre il
   * legacy. Se la voce non è nel menu dell'utente, ripiega sulla route Vue.
   *
   * @param query filtri da applicare sulla destinazione (es. `{ centroId: 12 }`).
   *   ⚠️ Vengono passati SOLO alla route Vue: le pagine Seam ricevono i filtri tramite
   *   stato di sessione impostato da un action listener (es. `controller.viewOspiti(centro)`
   *   fa `ospiteList.setCentro2(...)`), non da querystring, quindi un link esterno non può
   *   riprodurli — il portale classico si apre sulla lista non filtrata.
   */
  function openRouteOrLegacy(
    springRoute: string,
    routeName: string,
    query?: Record<string, string | number>,
    /**
     * Pagina Seam da aprire quando la funzione è legacy ma non ne dichiara una utilizzabile.
     * Serve per le funzioni-azione (un bottone dentro una pagina classica, senza indirizzo
     * proprio): il chiamante indica l'elenco da cui quel bottone si raggiunge.
     */
    fallbackLegacy?: string,
  ): void {
    const node = findNodeBySpringRoute(springRoute)
    if (node?.isLegacy && node.url) {
      openLegacyPage(node.url)
      return
    }
    // Non è una voce di menu (es. un bottone: `showHome=0`): l'albero non la contiene, ma il
    // catalogo `funzioni_2` sì. Senza questo passaggio il flag `is_legacy` non avrebbe effetto
    // su quelle funzioni.
    if (!node && funzioni.isLegacy(springRoute)) {
      const pagina = funzioni.paginaLegacy(springRoute) ?? fallbackLegacy
      if (pagina) {
        openLegacyPage(pagina)
        return
      }
    }
    void router.push({ name: routeName, query: query as Record<string, string> | undefined })
  }

  return { openRouteOrLegacy, findNodeBySpringRoute, openLegacyPage }
}
